import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { slugify, debug } from "helpers";
import { AllHtmlEntities as Entities } from "html-entities";
import TagManager from "react-gtm-module";

const appsettings = window.onlineStoreRuntimeConfig;
const StoreContext = React.createContext();
const { Provider, Consumer } = StoreContext;
const entities = new Entities();

const StoreProvider = props => {
  const { children } = props;
  const [config, setConfig] = useState({
    loading: false,
    loaded: false,
    error: null,
    configApiBaseUrl: appsettings.configApiBaseUrl,
    baseDomain: appsettings.baseDomain,
    subdomain: appsettings.subdomain,
    instanceName: appsettings.instanceName,
    appVersion: appsettings.appVersion,
    appId: null,
    envName: null,
    configName: null,
    applicationClientId: null,
    features: {},
    echannelApiDomainNameHeader: null,
    echannelApiBaseUrl: null,
    contactUsPardotForm: null,
    googleApiKey: null,
    gtmId: null,
    requestPricingPardotForm: null,
    identityPortalUrl: null,
    identityApiBaseUrl: null,
    storeCode: null,
    hstmApiKey: null
  });
  const [store, setStore] = useState({
    loading: false,
    loaded: false,
    error: null,
    portalId: null,
    catalogId: null,
    profileId: null,
    localeId: null,
    companyName: null,
    storeName: null,
    storeCode: null,
    catalogName: null,
    administratorEmail: null,
    salesEmail: null,
    customerServiceEmail: null,
    customerServicePhoneNumber: null,
    domainUrl: null,
    imageNotAvailablePath: null,
    salesPhoneNumber: null,
    mediaServerUrl: null,
    mediaServerThumbnailUrl: null,
    sliderBanners: null,
    sliderBannerSettings: null
  });
  const [categories, setCategories] = useState({
    loading: false,
    loaded: false,
    error: null,
    tree: [],
    list: []
  });
  const [partners, setPartners] = useState({
    loading: false,
    loaded: false,
    error: null,
    list: [],
    headLine: null
  });
  const [StoreAPIDefaults, setStoreAPIDefaults] = useState({
    baseURL: "",
    headers: {}
  });

  const getConfig = () => {
    setConfig({ ...config, ...{ loading: true } });
    const {
      configApiBaseUrl,
      baseDomain,
      subdomain,
      instanceName,
      configName,
      appVersion
    } = appsettings;
    const path = `${configApiBaseUrl}/${subdomain ||
      `${instanceName}-onlinestore`}/${baseDomain}/${configName}`;
    const token = localStorage.getItem("Hstm.Identity.AccessToken");

    axios
      .get(path)
      .then(response => {
        const { data } = response;
        const obj = {
          loading: false,
          loaded: true,
          error: null,
          appId: data.appId,
          envName: data.envName,
          configName: data.configName,
          applicationClientId: data.applicationClientId,
          appVersion,
          features: data.config.features,
          gtmId: data.gtmId,
          cookieBotUrl: data.config.cookieBotUrl,
          accessCourseLink: data.config.accessCourseLink,
          accessFreeCourseLink: data.config.accessFreeCourseLink,
          ...data.config
        };

        const features = {};
        obj.features.split("|").forEach(pair => {
          const kv = pair.split(":");
          features[kv[0]] = kv[1] === "true";
        });
        obj.features = features;

        if (obj.gtmId) {
          const tagManagerArgs = {
            gtmId: obj.gtmId
          };
          TagManager.initialize(tagManagerArgs);
        }

        setStoreAPIDefaults({
          baseURL: `${obj.echannelApiBaseUrl}/api`,
          headers: {
            Authorization: `Bearer ${token}`,
            "hstm-api-key": obj.hstmApiKey,
            "hstm-config-name": `${obj.envName}|${obj.appId}`
          }
        });

        window.localStorage.setItem("api", `${obj.echannelApiBaseUrl}/api`);
        window.localStorage.setItem(
          "domainname",
          obj.echannelApiDomainNameHeader
        );
        window.localStorage.setItem("apikey", obj.hstmApiKey);

        setConfig(obj);
      })
      .catch(err => {
        debug(err);
        const message = `There was a problem getting the store configuration.`;
        setConfig({
          ...config,
          ...{ loading: false, loaded: false, error: message }
        });
        debug("Webstore API Error Message:", message);
      });
  };

  const getStore = () => {
    setStore({ ...store, ...{ loading: true } });
    axios
      .get(`${config.echannelApiBaseUrl}/api/webstores/${config.storeCode}`, {
        headers: StoreAPIDefaults.headers
      })
      .then(response => {
        // debug(response);
        const { data } = response;

        const obj = {
          loading: false,
          loaded: true,
          error: null,
          portalId: data.portalId,
          catalogId: data.publishCatalogId,
          profileId: data.profileId,
          localeId: 1,
          companyName: data.companyName,
          storeName: data.storeName,
          storeCode: data.storeCode,
          catalogName: data.catalogName,
          administratorEmail: data.administratorEmail,
          salesEmail: data.salesEmail,
          customerServiceEmail: data.customerServiceEmail,
          customerServicePhoneNumber: data.customerServicePhoneNumber,
          domainUrl: data.domainUrl,
          imageNotAvailablePath: data.imageNotAvailablePath,
          salesPhoneNumber: data.salesPhoneNumber,
          mediaServerUrl: data.mediaServerUrl,
          mediaServerThumbnailUrl: data.mediaServerThumbnailUrl,
          sliderBanners: data.sliderBanners,
          sliderBannerSettings: data.sliderBannerSettings
        };

        setStore(obj);
      })
      .catch(error => {
        debug(error);
        const message = `There was a problem getting the store configuration.`;
        setStore({ ...store, ...{ loading: false, error: message } });
        debug("Webstore API Error Message:", message);
      });
  };

  const getCategories = () => {
    setCategories({ ...categories, ...{ loading: true } });
    axios
      .get("/catalog", StoreAPIDefaults)
      .then(response => {
        // debug(response);
        const { data } = response;
        const tree = [];
        const list = [];
        data.tree.children.forEach(l1 => {
          const catSlug =
            l1.seoDetails.seoPageName || slugify(l1.categoryTitle);
          const catText =
            l1.seoDetails.seoTitle || entities.decode(l1.categoryTitle);
          const cat = {
            id: l1.categoryId,
            slug: catSlug,
            text: catText,
            path: `/catalog/${catSlug}`,
            level: 1,
            order: l1.displayOrder,
            parent: 0,
            children: [],
            products: l1.productIds.length,
            darkIcon: l1.categoryIconDark,
            lightIcon: l1.categoryIconLight
          };
          if (l1.productIds.length > 0 && l1.children.length) {
            tree[cat.id] = cat;
            list[cat.id] = cat;
            if (l1.children.length > 0) {
              l1.children.forEach(l2 => {
                const subcatSlug =
                  l2.seoDetails.seoPageName || slugify(l2.categoryTitle);
                const subcatText =
                  l2.seoDetails.seoTitle || entities.decode(l2.categoryTitle);
                const subcat = {
                  id: l2.categoryId,
                  slug: subcatSlug,
                  text: subcatText,
                  path: `${cat.path}/${subcatSlug}`,
                  level: 2,
                  order: l2.displayOrder,
                  parent: l1.categoryId,
                  children: [],
                  products: l2.productIds.length
                };
                if (l2.productIds.length > 0) {
                  tree[cat.id].children[subcat.id] = subcat;
                  list[subcat.id] = subcat;
                }
              });
            }
            tree[cat.id].children.sort((a, b) => (a.order > b.order ? 1 : -1));
          }
        });
        tree.sort((a, b) => (a.order > b.order ? 1 : -1));
        setCategories({
          loading: false,
          loaded: true,
          error: null,
          tree,
          list
        });
      })
      .catch(err => {
        debug(err);
        const message = `There was a problem getting the list of categories.`;
        setCategories({ ...categories, ...{ loading: false, error: message } });
        debug("Catalog API Error Message:", message);
      });
  };

  const getCategoryByPath = path => {
    const catalog = {
      id: 0,
      text: "Catalog",
      path: "/catalog",
      parent: 0
    };
    const search = categories.list
      .filter(cat => {
        return cat.path === path;
      })
      .pop();
    const category = search || null;
    return path === "/catalog" ? catalog : category;
  };

  const getCategoryById = id => {
    const catalog = {
      id: 0,
      text: "Catalog",
      path: "/catalog",
      parent: 0
    };
    const search = categories.list
      .filter(cat => {
        return cat.id === id;
      })
      .pop();
    const category = search || null;
    return id === 0 ? catalog : category;
  };

  const sortPartnersByDisplayOrder = (a, b) => {
    if (a.displayOrder === b.displayOrder) {
      const NameA = a.nameFull.toUpperCase();
      const NameB = b.nameFull.toUpperCase();
      if (NameA < NameB) {
        return -1;
      }
      return NameA > NameB ? 1 : 0;
    }
    return a.displayOrder > b.displayOrder ? 1 : -1;
  };

  const getPartners = () => {
    setPartners({ ...partners, ...{ loading: true } });
    axios
      .get("/partners?pagesize=-1&pageNumber=1", StoreAPIDefaults)
      .then(response => {
        const { data } = response;
        const list = data.partners.sort(sortPartnersByDisplayOrder);
        setPartners({
          loading: false,
          loaded: true,
          error: null,
          list,
          headLine: data.headLine
        });
      })
      .catch(err => {
        debug(err);
        const message = `There was a problem getting the list of partners.`;
        setPartners({ ...partners, ...{ loading: false, error: message } });
        debug("Partners API Error Message:", message);
      });
  };

  const getPartnerByCode = code => {
    const partner = partners.list
      .filter(item => {
        return item.code === code;
      })
      .pop();
    return partner || null;
  };

  const StoreAPI = axios.create(StoreAPIDefaults);

  useEffect(() => {
    if (!config.loaded) {
      getConfig();
    }
    if (config.loaded && !store.loaded) {
      getStore();
    }
    if (config.loaded && store.loaded) {
      getCategories();
      getPartners();
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line
  }, [config.loaded, store.loaded]);

  return (
    <Provider
      value={{
        config,
        store,
        categories,
        getCategoryByPath,
        getCategoryById,
        partners,
        getPartnerByCode,
        StoreAPI
      }}
    >
      {children}
    </Provider>
  );
};

StoreProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export { StoreProvider, Consumer as StoreConsumer, StoreContext };
